/**
 * Moderation Page for Comments Press Zone admin.
 * Ported from Comments Press Zone bans.js with naming convention changes.
 */
import { el, mount, clear, __, showLoading, formatDate } from '../utils/dom.js';
import API from '../utils/api.js';
import { Button } from '../components/Button.js';
import { Tabs, TabPanel } from '../components/Tabs.js';
import GridTable from '../components/GridTable.js';
import Confirm from '../utils/confirm.js';
import showInfractionModal, { getSuggestedActionLabel } from '../components/InfractionModal.js';
import { EmptyState } from '../components/EmptyState.js';

let activeTab = 'overview';
let slideDirection = null; // Track animation direction for tab transitions
let pendingUserId = null;
let pendingAction = null;
let reportsFilter = 'open';
let reports = [];
let actionLoading = null;

/**
 * Parse query parameters from the hash.
 * e.g., #/bans?user=5&action=ban -> { user: '5', action: 'ban' }
 */
function getHashParams() {
    const hash = window.location.hash;
    const queryIndex = hash.indexOf('?');
    if (queryIndex === -1) return {};

    const queryString = hash.substring(queryIndex + 1);
    const params = {};
    queryString.split('&').forEach(pair => {
        const [key, value] = pair.split('=');
        if (key) {
            params[decodeURIComponent(key)] = value ? decodeURIComponent(value) : '';
        }
    });
    return params;
}

/**
 * Render the Moderation page.
 * @param {HTMLElement} container - The container element.
 * @param {Function} addNotice - Notice callback
 * @param {string} subPath - The current sub-tab from URL
 */
export async function renderModeration(container, addNotice, subPath = '') {
    // Map subPath to activeTab if valid
    const validTabs = ['overview', 'reports', 'warnings', 'warned', 'muted', 'bans'];

    // Parse URL parameters
    const params = getHashParams();
    const prevTab = activeTab;

    // Determine current tab from subPath OR params
    let nextTab = activeTab;
    const tabName = subPath.split('?')[0];

    if (tabName && validTabs.includes(tabName)) {
        nextTab = tabName;
    } else if (params.user) {
        nextTab = 'warnings';
    } else if (!subPath) {
        nextTab = 'overview';
    }

    activeTab = nextTab;

    // Handle user parameter
    if (params.user) {
        pendingUserId = parseInt(params.user, 10);
        pendingAction = params.action || null;
    }

    // Optimization: If shell already rendered, handle partial update
    const hasShell = container.querySelector('.presszone-comments-tab-content');
    if (hasShell) {
        // Update tab active states visually immediately
        container.querySelectorAll('.presszone-comments-tabs__item').forEach(tabEl => {
            const isActive = tabEl.dataset.tab === activeTab;
            tabEl.classList.toggle('presszone-comments-tabs__item--active', isActive);
            tabEl.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });

        if (prevTab !== activeTab) {
            const contentArea = document.getElementById('presszone-comments-moderation-content');
            if (contentArea) {
                // Clear and render with animation
                clear(contentArea);
                const panel = TabPanel({
                    content: await renderTabContentWithAnimation(),
                    direction: slideDirection,
                    className: 'presszone-comments-moderation__panel-wrapper'
                });
                mount(contentArea, panel);
                slideDirection = null; // Reset
            }
        } else if (pendingUserId) {
            const resultsContainer = document.getElementById('presszone-comments-warnings-results');
            if (resultsContainer) {
                const userIdToLoad = pendingUserId;
                const actionToTake = pendingAction;
                pendingUserId = null;
                pendingAction = null;
                await loadUserByIdAndShow(userIdToLoad, resultsContainer, actionToTake);
            }
        }
        return;
    }

    // Full render of the page structure (only on initial load or page switch)
    container.innerHTML = '';

    // Tab definitions for moderation page
    const tabDefs = [
        { id: 'overview', label: __('Overview', 'comments-press-zone'), icon: '📊' },
        { id: 'reports', label: __('Reports', 'comments-press-zone'), icon: '📋' },
        { id: 'warnings', label: __('User Lookup', 'comments-press-zone'), icon: '🔍' },
        { id: 'warned', label: __('Warned', 'comments-press-zone'), icon: '⚠️' },
        { id: 'muted', label: __('Muted', 'comments-press-zone'), icon: '🔇' },
        { id: 'bans', label: __('Banned', 'comments-press-zone'), icon: '🚫' }
    ];

    const header = el('header', { class: 'presszone-comments-settings__header' },
        el('h1', {}, __('Moderation', 'comments-press-zone'))
    );

    const tabsEl = Tabs({
        tabs: tabDefs,
        activeTab,
        onChangeWithDirection: (id, direction) => {
            slideDirection = direction;
            window.location.hash = `#/moderation/${id}`;
        },
        onChange: () => { }
    });

    const content = el('div', { class: 'presszone-comments-tab-content', id: 'presszone-comments-moderation-content' });

    mount(container, header);
    mount(container, tabsEl);
    mount(container, content);

    // Render tab content with slide animation
    async function renderTabContentWithAnimation() {
        const tabContentEl = el('div', { class: 'presszone-comments-moderation__panel' });

        try {
            if (activeTab === 'overview') {
                await renderOverviewTab(tabContentEl);
            } else if (activeTab === 'reports') {
                await renderReportsTab(tabContentEl);
            } else if (activeTab === 'bans') {
                await renderInfractionUsersTab(tabContentEl, 'ban');
            } else if (activeTab === 'muted') {
                await renderInfractionUsersTab(tabContentEl, 'mute');
            } else if (activeTab === 'warned') {
                await renderInfractionUsersTab(tabContentEl, 'warning');
            } else {
                await renderWarningsTab(tabContentEl);
            }
        } catch (error) {
            console.error('Error rendering tab content:', error);
            tabContentEl.innerHTML = `<div class="presszone-comments-error"><p>${__('Error loading tab content.', 'comments-press-zone')}</p></div>`;
        }

        return tabContentEl;
    };

    // Wrap content in animated TabPanel
    const panel = TabPanel({
        content: await renderTabContentWithAnimation(),
        direction: slideDirection,
        className: 'presszone-comments-moderation__panel-wrapper'
    });

    mount(content, panel);

    // Reset direction after render
    slideDirection = null;
}

// ============================================================================
// OVERVIEW TAB
// ============================================================================

/**
 * Render the Overview tab with stats cards and activity feed.
 */
async function renderOverviewTab(container) {
    // Stats cards row
    const statsRow = el('div', {
        class: 'presszone-comments-stats-row',
        style: 'display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 16px; margin-top: 24px;'
    });

    // Placeholder stats while loading
    const placeholderStats = [
        { icon: '🚫', label: __('Banned', 'comments-press-zone'), value: '...', variant: 'red' },
        { icon: '🔇', label: __('Muted', 'comments-press-zone'), value: '...', variant: 'orange' },
        { icon: '⚠️', label: __('Warned', 'comments-press-zone'), value: '...', variant: 'yellow' },
        { icon: '📋', label: __('Reports', 'comments-press-zone'), value: '...', variant: 'blue' }
    ];

    placeholderStats.forEach(stat => {
        mount(statsRow, renderStatCard(stat.icon, stat.value, stat.label, stat.variant));
    });

    mount(container, statsRow);

    // Load actual stats
    try {
        const stats = await API.get('/moderation/stats');
        statsRow.innerHTML = '';

        const statCards = [
            { icon: '🚫', label: __('Banned', 'comments-press-zone'), value: stats.banned_count || 0, variant: 'red' },
            { icon: '🔇', label: __('Muted', 'comments-press-zone'), value: stats.muted_count || 0, variant: 'orange' },
            { icon: '⚠️', label: __('Warned', 'comments-press-zone'), value: stats.warned_count || 0, variant: 'yellow' },
            { icon: '📋', label: __('Reports', 'comments-press-zone'), value: stats.pending_reports || 0, variant: 'blue' }
        ];

        statCards.forEach(stat => {
            mount(statsRow, renderStatCard(stat.icon, stat.value, stat.label, stat.variant));
        });
    } catch (err) {
        console.error('Failed to load moderation stats:', err);
    }

    // Quick actions row
    const actionsRow = el('div', {
        style: 'display: flex; gap: 12px; margin-top: 24px; flex-wrap: wrap;'
    });

    mount(actionsRow, el('button', {
        class: 'presszone-comments-btn presszone-comments-btn--primary',
        onclick: () => showInfractionModal(null, container, { title: __('Ban a User', 'comments-press-zone'), initialType: 'ban' })
    }, '🚫 ' + __('Ban a User', 'comments-press-zone')));

    mount(actionsRow, el('button', {
        class: 'presszone-comments-btn presszone-comments-btn--secondary',
        onclick: () => showInfractionModal(null, container, { title: __('Issue Warning', 'comments-press-zone'), initialType: 'warning' })
    }, '⚠️ ' + __('Issue Warning', 'comments-press-zone')));

    mount(container, actionsRow);

    // Activity feed section
    const activitySection = el('div', { style: 'margin-top: 32px;' });
    mount(activitySection, el('h3', { style: 'margin: 0 0 16px 0; font-size: 16px; font-weight: 600;' },
        __('Recent Activity', 'comments-press-zone')
    ));

    const activityContainer = el('div');
    mount(activitySection, activityContainer);
    mount(container, activitySection);

    // Load activity feed
    try {
        const activities = await API.get('/moderation/activity?limit=15');
        
        // Define columns for GridTable
        const columns = [
            { label: '', width: '50px' }, // Icon
            { label: __('User', 'comments-press-zone'), flex: 1.5, minWidth: '150px' },
            { label: __('Action', 'comments-press-zone'), flex: 2, minWidth: '200px' },
            { label: __('Time', 'comments-press-zone'), width: '140px' },
            { label: __('Moderator', 'comments-press-zone'), width: '140px' }
        ];

        const table = new GridTable({
            columns,
            emptyMessage: __('No recent moderation activity', 'comments-press-zone'),
            className: 'presszone-comments-grid-table presszone-comments-activity-table' // Add specific class if needed
        });

        const tableEl = table.render(activities, renderActivityRow);
        mount(activityContainer, tableEl);

    } catch (err) {
        console.error('Failed to load activity feed:', err);
        mount(activityContainer, el('div', { class: 'presszone-comments-status-message presszone-comments-status-message--error' },
            __('Failed to load activity', 'comments-press-zone')
        ));
    }
}

/**
 * Render a stat card for the overview.
 */
function renderStatCard(icon, value, label, variant) {
    return el('div', {
        class: `presszone-comments-stat-card presszone-comments-stat-card--${variant}`
    },
        el('div', { class: 'presszone-comments-stat-card__icon' }, icon),
        el('div', { class: 'presszone-comments-stat-card__value' }, String(value)),
        el('div', { class: 'presszone-comments-stat-card__label' }, label)
    );
}

/**
 * Render an activity feed row for GridTable.
 * Returns array of cell contents.
 */
function renderActivityRow(activity) {
    let icon = '📋';
    const action = (activity.action || activity.type || '').toLowerCase();
    
    // Determine icon based on action
    if (action.includes('unban')) {
        icon = '✅'; 
    } else if (action.includes('unmute')) {
        icon = '🔊'; 
    } else if (action.includes('ban')) {
        icon = '🚫'; 
    } else if (action.includes('mute')) {
        icon = '🔇'; 
    } else if (action.includes('clear') || action.includes('delete')) {
        icon = '🛡️'; 
    } else if (action.includes('warn')) {
        icon = '⚠️'; 
    }

    const cellContents = [];

    // 1. Icon
    cellContents.push(el('span', { style: 'font-size: 18px;' }, icon));

    // 2. User
    cellContents.push(
        el('div', { class: 'presszone-comments-user-cell' },
            // el('img', { src: activity.user_avatar || '', class: 'presszone-comments-avatar' }), // Add avatar if available in API
            el('div', { class: 'presszone-comments-user-info' },
                el('a', { 
                    href: `#/moderation?user=${activity.user_id}`,
                    class: 'presszone-comments-user-name',
                    style: 'text-decoration: none; color: inherit;' // Reset link style
                }, activity.user_name || 'Unknown User')
            )
        )
    );

    // 3. Action / Reason
    cellContents.push(
        el('div', { class: 'presszone-comments-reason-cell' },
            el('div', { class: 'presszone-comments-reason-text', style: 'font-weight: 500;' }, activity.action),
            activity.reason ? el('div', { 
                class: 'presszone-comments-reason-note',
                style: 'margin-top: 2px;'
            }, activity.reason) : null
        )
    );

    // 4. Time
    const timeAgo = activity.time_ago || formatDate(activity.created_at);
    cellContents.push(el('span', { class: 'presszone-comments-date-text' }, timeAgo));

    // 5. Moderator
    cellContents.push(activity.moderator_name || 'System');

    return cellContents;
}

// ============================================================================
// REPORTS TAB
// ============================================================================

/**
 * Render the Reports tab.
 */
async function renderReportsTab(container) {
    showLoading(container, __('Loading reports...', 'comments-press-zone'));

    try {
        const data = await API.get(`/reports?status=${reportsFilter}`);
        reports = data.reports || [];
        renderReportsContent(container);
    } catch (error) {
        clear(container);
        container.appendChild(
            el('div', { class: 'presszone-comments-error' },
                el('h2', {}, __('Error loading reports', 'comments-press-zone')),
                el('p', {}, error.message)
            )
        );
    }
}

/**
 * Render the reports content with filters and list.
 */
function renderReportsContent(container) {
    clear(container);

    // Filter tabs
    const filterTabs = el('div', { class: 'presszone-comments-filter-tabs', style: 'display: flex; gap: 8px; margin: 16px 0;' },
        el('button', {
            class: `presszone-comments-btn presszone-comments-btn--sm ${reportsFilter === 'open' ? 'presszone-comments-btn--primary' : 'presszone-comments-btn--ghost'}`,
            onclick: () => { reportsFilter = 'open'; renderReportsTab(container); }
        }, __('Open', 'comments-press-zone')),
        el('button', {
            class: `presszone-comments-btn presszone-comments-btn--sm ${reportsFilter === 'resolved' ? 'presszone-comments-btn--primary' : 'presszone-comments-btn--ghost'}`,
            onclick: () => { reportsFilter = 'resolved'; renderReportsTab(container); }
        }, __('Resolved', 'comments-press-zone')),
        el('button', {
            class: `presszone-comments-btn presszone-comments-btn--sm ${reportsFilter === 'rejected' ? 'presszone-comments-btn--primary' : 'presszone-comments-btn--ghost'}`,
            onclick: () => { reportsFilter = 'rejected'; renderReportsTab(container); }
        }, __('Dismissed', 'comments-press-zone'))
    );

    container.appendChild(filterTabs);

    // Reports list or empty state
    if (reports.length === 0) {
        container.appendChild(
            EmptyState(
                '✨',
                __('All clear!', 'comments-press-zone'),
                __('No reports in this category.', 'comments-press-zone')
            )
        );
    } else {
        const list = el('div', { class: 'presszone-comments-moderation__list' },
            ...reports.map(report => renderReportCard(report, container))
        );
        container.appendChild(list);
    }
}

/**
 * Render a report card.
 */
function renderReportCard(report, container) {
    const card = el('div', { class: 'presszone-comments-moderation__card' },
        // Header (Type & Date)
        el('div', { class: 'presszone-comments-moderation__card-header' },
            el('span', { class: 'presszone-comments-moderation__type' },
                (report.content_type === 'post' ? '💬' : '📄') + ' ' +
                (report.content_type === 'post' ? __('COMMENT', 'comments-press-zone') : __('THREAD', 'comments-press-zone')) + ' #' + report.content_id
            ),
            el('span', { class: 'presszone-comments-moderation__date' }, formatDate(report.report_date))
        ),

        // Content preview
        el('div', { class: 'presszone-comments-moderation__content' },
            el('p', { class: 'presszone-comments-moderation__preview' },
                report.content_preview || __('[Content unavailable]', 'comments-press-zone')
            )
        ),

        // Reason
        el('div', { class: 'presszone-comments-moderation__reason' },
            el('strong', {}, __('Reason:', 'comments-press-zone')),
            el('p', {}, report.reason)
        ),

        // Meta
        el('div', { class: 'presszone-comments-moderation__meta' },
            el('span', {}, __('Reported by:', 'comments-press-zone') + ' ' + (report.reporter_name || __('Unknown', 'comments-press-zone'))),
            report.status !== 'open' && report.resolver_name ?
                el('span', { style: 'margin-inline-start: 16px;' },
                    (report.status === 'resolved' ? __('Resolved by:', 'comments-press-zone') : __('Dismissed by:', 'comments-press-zone')) +
                    ' ' + report.resolver_name,
                    report.resolve_date ? el('span', { style: 'opacity: 0.7; margin-inline-start: 8px;' }, '(' + formatDate(report.resolve_date) + ')') : null
                ) : null
        ),

        // Actions (only for open reports)
        reportsFilter === 'open' ?
            el('div', { class: 'presszone-comments-moderation__actions' },
                Button({
                    label: actionLoading === report.report_id ? '...' : '✓ ' + __('Resolve', 'comments-press-zone'),
                    variant: 'success',
                    size: 'sm',
                    disabled: actionLoading === report.report_id,
                    onClick: () => handleReportAction(report.report_id, 'resolve', container)
                }),
                Button({
                    label: '✕ ' + __('Dismiss', 'comments-press-zone'),
                    variant: 'ghost',
                    size: 'sm',
                    disabled: actionLoading === report.report_id,
                    onClick: () => handleReportAction(report.report_id, 'dismiss', container)
                }),
                Button({
                    label: '🗑 ' + __('Delete Content', 'comments-press-zone'),
                    variant: 'danger',
                    size: 'sm',
                    disabled: actionLoading === report.report_id,
                    onClick: async () => {
                        if (await Confirm(__('Delete this content? This cannot be undone.', 'comments-press-zone'), { okVariant: 'danger', icon: '🗑️' })) {
                            handleReportAction(report.report_id, 'delete-content', container);
                        }
                    }
                }),
                (report.is_first_post || report.content_type === 'thread') ?
                    Button({
                        label: '🗑️📄 ' + __('Delete Thread', 'comments-press-zone'),
                        size: 'sm',
                        attrs: { class: 'presszone-comments-btn presszone-comments-btn--sm presszone-comments-btn--danger-gradient' },
                        disabled: actionLoading === report.report_id,
                        onClick: async () => {
                            if (await Confirm(__('Delete the ENTIRE COMMENT THREAD and all its comments? This cannot be undone!', 'comments-press-zone'), { okVariant: 'danger', icon: '💀' })) {
                                handleReportAction(report.report_id, 'delete-thread', container);
                            }
                        }
                    }) : null
            ) : null
    );

    return card;
}

/**
 * Handle report action.
 */
async function handleReportAction(reportId, action, container) {
    actionLoading = reportId;
    renderReportsContent(container);

    try {
        await API.post(`/reports/${reportId}/${action}`);
        await renderReportsTab(container);
    } catch (error) {
        if (typeof window.addNotice === 'function') {
            window.addNotice(__('Action failed:', 'comments-press-zone') + ' ' + error.message, 'error');
        }
    } finally {
        actionLoading = null;
    }
}

// ============================================================================
// SHARED INFRACTION USERS TAB (Banned/Muted/Warned)
// ============================================================================

/**
 * Configuration for each infraction type.
 */
const INFRACTION_CONFIG = {
    ban: {
        title: __('Banned Users', 'comments-press-zone'),
        icon: '🚫',
        endpoint: '/bans',
        emptyMessage: __('No banned users.', 'comments-press-zone'),
        emptyIcon: '🚫',
        emptyTitle: __('No Banned Users', 'comments-press-zone'),
        emptyDescription: __('Banned users will appear here.', 'comments-press-zone'),
        actionLabel: __('Ban a User', 'comments-press-zone'),
        removeLabel: __('Unban', 'comments-press-zone'),
        removeAction: 'unban',
        issuedByLabel: __('Banned By', 'comments-press-zone'),
        reasonField: 'ban_reason',
        durationField: 'ban_duration_human',
        expiresField: 'ban_expires_human',
        timeLeftField: 'ban_time_left_formatted',
        issuedByField: 'banned_by',
        badgeClass: 'presszone-comments-badge--danger'
    },
    mute: {
        title: __('Muted Users', 'comments-press-zone'),
        icon: '🔇',
        endpoint: '/mutes',
        emptyMessage: __('No muted users.', 'comments-press-zone'),
        emptyIcon: '🔇',
        emptyTitle: __('No Muted Users', 'comments-press-zone'),
        emptyDescription: __('Muted users will appear here.', 'comments-press-zone'),
        actionLabel: __('Mute a User', 'comments-press-zone'),
        removeLabel: __('Unmute', 'comments-press-zone'),
        removeAction: 'unmute',
        issuedByLabel: __('Muted By', 'comments-press-zone'),
        reasonField: 'mute_reason',
        durationField: 'mute_duration_human',
        expiresField: 'mute_expires_human',
        timeLeftField: 'mute_time_left_formatted',
        issuedByField: 'muted_by',
        badgeClass: 'presszone-comments-badge--link'
    },
    warning: {
        title: __('Warned Users', 'comments-press-zone'),
        icon: '⚠️',
        endpoint: '/warnings/active',
        emptyMessage: __('No warned users.', 'comments-press-zone'),
        emptyIcon: '⚠️',
        emptyTitle: __('No Warned Users', 'comments-press-zone'),
        emptyDescription: __('Warned users will appear here.', 'comments-press-zone'),
        actionLabel: __('Issue Warning', 'comments-press-zone'),
        removeLabel: __('Clear', 'comments-press-zone'),
        removeAction: 'clear-warning',
        issuedByLabel: __('Warned By', 'comments-press-zone'),
        reasonField: 'warning_reason',
        durationField: null, // Warnings don't have duration
        expiresField: 'warning_expires_human',
        timeLeftField: null,
        issuedByField: 'warned_by',
        badgeClass: 'presszone-comments-badge--warning'
    }
};

/**
 * Render the shared infraction users tab.
 * @param {HTMLElement} container 
 * @param {string} type - 'ban', 'mute', or 'warning'
 */
async function renderInfractionUsersTab(container, type) {
    try {
        const config = INFRACTION_CONFIG[type];

        const headerRow = el('div', { class: 'presszone-comments-settings__header', style: 'margin-top: 24px;' },
            el('h2', { style: 'margin: 0; font-size: 18px;' }, config.icon + ' ' + config.title),
            Button({
                label: config.actionLabel,
                variant: 'primary',
                onClick: () => showInfractionModal(null, container, { title: config.actionLabel, initialType: type })
            })
        );

        const tableContainer = el('div', { class: 'presszone-comments-table-container', id: `${type}-table-container` });

        mount(container, headerRow);
        mount(container, tableContainer);

        loadInfractionUsers(tableContainer, type);
    } catch (error) {
        console.error('Error rendering infraction tab:', error);
        container.innerHTML = '<p class="presszone-comments-error">' + __('Error initializing tab.', 'comments-press-zone') + '</p>';
    }
}

/**
 * Load users with active infractions.
 */
async function loadInfractionUsers(container, type) {
    const config = INFRACTION_CONFIG[type];

    container.innerHTML = '<p class="presszone-comments-loading">' + __('Loading...', 'comments-press-zone') + '</p>';

    try {
        const users = await API.get(config.endpoint);

        if (!users || !Array.isArray(users)) {
            container.innerHTML = '<p class="presszone-comments-error">' + __('Failed to load.', 'comments-press-zone') + '</p>';
            return;
        }

        // GridTable is now imported at top level

        if (users.length === 0) {
            // Show empty state
            const emptyState = new GridTable({
                columns: [], // Empty columns for empty state
                emptyMessage: config.emptyMessage,
                emptyIcon: config.emptyIcon,
                emptyTitle: config.emptyTitle,
                emptyDescription: config.emptyDescription
            });
            container.innerHTML = '';
            mount(container, emptyState.render([], () => { }));
            return;
        }

        // Build columns - 5 columns for consistent layout
        const columns = [
            { label: __('User', 'comments-press-zone'), flex: 2, minWidth: '180px' },
            { label: __('Reason', 'comments-press-zone'), flex: 3, minWidth: '200px' }
        ];

        if (type === 'warning') {
            // Warnings: show when it was issued
            columns.push({ label: __('Warned On', 'comments-press-zone'), flex: 1.5, minWidth: '120px' });
        } else {
            // Mutes/Bans: show duration and expiration
            columns.push({ label: __('Duration', 'comments-press-zone'), flex: 1, minWidth: '110px' });
            columns.push({ label: __('Expires', 'comments-press-zone'), flex: 1.2, minWidth: '145px' });
        }

        columns.push({ label: config.issuedByLabel, flex: 1.2, minWidth: '140px' });
        columns.push({ label: __('Actions', 'comments-press-zone'), width: '140px' });

        const tableComponent = new GridTable({
            columns,
            emptyMessage: config.emptyMessage,
            emptyIcon: config.emptyIcon,
            emptyTitle: config.emptyTitle,
            emptyDescription: config.emptyDescription
        });

        const tableEl = tableComponent.render(users, (user) => renderInfractionUserRow(user, container, type, columns));

        container.innerHTML = '';
        mount(container, tableEl);

    } catch (error) {
        container.innerHTML = '<p class="presszone-comments-error">' + __('Failed to load.', 'comments-press-zone') + ' ' + error.message + '</p>';
        console.error(`Error loading ${type} users:`, error);
    }
}

/**
 * Render a row for an infraction user.
 * Returns an array of cell CONTENTS (not wrapped cells).
 * GridTable will handle wrapping each content in a cell element.
 */
function renderInfractionUserRow(user, tableContainer, type, _columns) {
    const config = INFRACTION_CONFIG[type];
    const rowActions = [];

    // View history button
    rowActions.push(
        Button({
            label: '📋',
            variant: 'secondary',
            size: 'sm',
            title: __('View History', 'comments-press-zone'),
            onClick: (e) => {
                e.stopPropagation();
                viewUserHistory(user.id);
            }
        })
    );

    // Remove action button
    rowActions.push(
        Button({
            label: config.removeLabel,
            variant: 'success',
            size: 'sm',
            onClick: (e) => {
                e.stopPropagation();
                removeInfraction(user.id, type, user.infraction_id, tableContainer);
            }
        })
    );

    // Build array of cell contents (GridTable will wrap each in a cell)
    const cellContents = [];

    // 1. User Cell
    cellContents.push(
        el('div', { class: 'presszone-comments-user-cell' },
            el('img', { src: user.avatar || '', alt: '', class: 'presszone-comments-avatar' }),
            el('div', { class: 'presszone-comments-user-info' },
                el('span', { class: 'presszone-comments-user-name' }, user.display_name || __('Unknown', 'comments-press-zone')),
                el('span', { class: 'presszone-comments-user-handle' }, '@' + (user.username || 'unknown')),
                user.warning_count > 0 && type !== 'warning' ? el('span', {
                    class: 'presszone-comments-badge presszone-comments-badge--warning'
                }, `⚠️ ${user.warning_count}`) : null
            )
        )
    );

    // 2. Reason Cell
    cellContents.push(
        el('div', { class: 'presszone-comments-reason-cell' },
            el('div', { class: 'presszone-comments-reason-text' }, user[config.reasonField] || user.reason || __('No reason provided', 'comments-press-zone')),
            (user.notes || user.moderator_notes) ? el('div', { class: 'presszone-comments-reason-note' },
                el('span', { class: 'presszone-comments-reason-note-label' }, '📝 ' + __('Internal Note:', 'comments-press-zone')),
                ' ' + (user.notes || user.moderator_notes)
            ) : null
        )
    );

    // 3. Duration/Date columns based on type
    if (type === 'warning') {
        // Warnings: show "Warned On" date
        const warnedDate = user.created_at || user.warned_on || user.date;
        cellContents.push(
            el('span', { class: 'presszone-comments-date-text' }, warnedDate ? formatDate(warnedDate) : '-')
        );
    } else {
        // Mutes/Bans: show Duration
        cellContents.push(
            user.is_permanent
                ? el('span', { class: `presszone-comments-badge ${config.badgeClass}` }, __('Permanent', 'comments-press-zone'))
                : (user[config.durationField] || user.duration_human || '-')
        );

        // Mutes/Bans: show Expires
        cellContents.push(
            user.is_permanent
                ? el('span', { class: `presszone-comments-badge ${config.badgeClass}` }, __('Never', 'comments-press-zone'))
                : el('span', { class: 'presszone-comments-badge' }, user[config.timeLeftField] || user[config.expiresField] || user.expires_human || '-')
        );
    }

    // 5. Issued By
    cellContents.push(user[config.issuedByField] || user.issued_by || '-');

    // 6. Actions
    cellContents.push(el('div', { class: 'presszone-comments-actions-cell' }, ...rowActions));

    // Return array of cell contents (not wrapped in cells - GridTable handles that)
    return cellContents;
}


/**
 * Remove an infraction (unban, unmute, clear warning).
 */
async function removeInfraction(userId, type, infractionId, tableContainer) {
    const _config = INFRACTION_CONFIG[type];
    const confirmMessage = type === 'ban'
        ? __('Are you sure you want to unban this user?', 'comments-press-zone')
        : type === 'mute'
            ? __('Are you sure you want to unmute this user?', 'comments-press-zone')
            : __('Are you sure you want to clear this warning?', 'comments-press-zone');

    if (!await Confirm(confirmMessage, { okVariant: 'danger', icon: '⚠️' })) {
        return;
    }

    try {
        if (type === 'ban') {
            await API.post('/unban', { user_id: userId });
        } else if (type === 'mute') {
            await API.post('/unmute', { user_id: userId });
        } else if (type === 'warning' && infractionId) {
            await API.delete(`/warnings/${infractionId}`);
        }
        loadInfractionUsers(tableContainer, type);
    } catch (error) {
        window.addNotice?.(__('Failed to remove infraction.', 'comments-press-zone') + ' ' + error.message, 'error');
        console.error('Error removing infraction:', error);
    }
}

/**
 * View a user's full moderation history.
 */
function viewUserHistory(userId) {
    // Navigate to User Lookup tab with the user parameter in URL
    // The router will handle loading the user's warnings
    window.location.hash = `#/moderation/warnings?user=${userId}`;
}


/**
 * Load a user by ID and show their warnings in the results container.
 * Optionally trigger an action like 'ban' or 'warn'.
 */
async function loadUserByIdAndShow(userId, resultsContainer, action = null) {
    if (!resultsContainer || resultsContainer === document.body) {
        // If container is body, we are likely called from frontend via global modal
        // In this case, we don't render the results list here as there's no suitable container.
        // We just ensure the modal knows about the user.
        return;
    }

    resultsContainer.innerHTML = '<p class="presszone-comments-loading">' + __('Loading user...', 'comments-press-zone') + '</p>';

    try {
        // Optimization: Fetch user and warnings in parallel
        const [users, warningsData] = await Promise.all([
            API.get(`/users?include=${userId}`),
            API.get(`/users/${userId}/warnings`)
        ]);

        if (users && users.length > 0) {
            const user = users[0];

            // Update the search input to show the user
            const searchInput = document.getElementById('warning-user-search');
            if (searchInput) {
                searchInput.value = user.display_name + ' (@' + user.username + ')';
            }

            // Show the user's warnings using the already fetched data
            await renderUserWarnings(user, warningsData, resultsContainer);

            // If a specific action was requested, trigger it
            if (action === 'ban') {
                showInfractionModal(user.id, resultsContainer, { title: __('Ban User', 'comments-press-zone'), initialType: 'ban' });
            } else if (action === 'warn') {
                showInfractionModal(user.id, resultsContainer);
            }
        } else {
            resultsContainer.innerHTML = '<p class="presszone-comments-error">' + __('User not found.', 'comments-press-zone') + '</p>';
        }
    } catch (err) {
        console.error('Failed to load user info:', err);
        resultsContainer.innerHTML = '<p class="presszone-comments-error">' + __('Failed to load user.', 'comments-press-zone') + '</p>';
    }
}

// ============================================================================
// WARNINGS TAB
// ============================================================================

async function renderWarningsTab(container) {
    let _searchTimeout = null;
    const { default: UserAutocomplete } = await import('../components/UserAutocomplete.js');

    const searchSection = el('div', { class: 'presszone-comments-search-section' },
        el('h2', { style: 'margin-top: 0;' }, __('User Warnings', 'comments-press-zone')),
        el('p', { class: 'presszone-comments-text-muted' }, __('Search for a user to view or issue warnings.', 'comments-press-zone')),
        UserAutocomplete({
            id: 'warning-user-search',
            placeholder: __('Start typing username or email...', 'comments-press-zone'),
            containerStyle: { maxWidth: '500px' },
            onSelect: (user) => showUserWarnings(user, resultsContainer)
        })
    );

    const resultsContainer = el('div', { id: 'warnings-results' });

    mount(container, searchSection);
    mount(container, resultsContainer);

    // If there's a pending user from URL, load them automatically
    if (pendingUserId) {
        const userIdToLoad = pendingUserId;
        const actionToTake = pendingAction;

        // Clear pending state to avoid reloading on tab switch
        pendingUserId = null;
        pendingAction = null;

        // Load the user
        loadUserByIdAndShow(userIdToLoad, resultsContainer, actionToTake);
    }
}

/**
 * Fetch and show user warnings.
 */
async function showUserWarnings(user, container) {
    if (!container || container === document.body) return;

    container.innerHTML = '<p class="presszone-comments-loading">' + __('Loading warnings...', 'comments-press-zone') + '</p>';

    try {
        const data = await API.get(`/users/${user.id}/warnings`);
        await renderUserWarnings(user, data, container);
    } catch (error) {
        container.innerHTML = '<p class="presszone-comments-error">' + __('Failed to load warnings.', 'comments-press-zone') + '</p>';
        console.error('Error loading warnings:', error);
    }
}

/**
 * Render user warnings data into container.
 */
async function renderUserWarnings(user, data, container) {
    if (!container || container === document.body) return;

    container.innerHTML = '';

    const summary = data.infraction_summary || {};
    const escalationLevel = data.escalation_level || 0;
    const suggested = summary.suggested_action || {};

    // Escalation level visualization
    const escalationBar = el('div', {
        style: 'display: flex; align-items: center; gap: 8px; margin-bottom: 16px; padding: 16px; background: var(--presszone-comments-surface); border-radius: var(--presszone-comments-radius); border: 1px solid var(--presszone-comments-border);'
    },
        el('span', { style: 'font-size: 13px; font-weight: 500; color: var(--presszone-comments-text-muted); margin-inline-end: 12px;' }, __('Escalation:', 'comments-press-zone')),
        ...renderEscalationSteps(escalationLevel)
    );

    // Suggested action card
    const suggestedCard = suggested.type ? el('div', {
        style: 'display: flex; align-items: center; gap: 12px; padding: 12px 16px; background: rgba(245, 158, 11, 0.1); border: 1px solid var(--presszone-comments-warning); border-radius: var(--presszone-comments-radius); margin-bottom: 16px;'
    },
        el('span', { style: 'font-size: 18px;' }, '💡'),
        el('div', { style: 'flex: 1;' },
            el('strong', { style: 'color: var(--presszone-comments-warning);' }, __('Suggested:', 'comments-press-zone') + ' '),
            el('span', {}, getSuggestedActionLabel(suggested)),
            el('p', { style: 'margin: 4px 0 0; font-size: 12px; color: var(--presszone-comments-text-muted);' }, suggested.rationale || '')
        )
    ) : null;

    // Build status badges based on current infractions
    const statusBadges = [];
    if (summary.active_bans > 0) {
        statusBadges.push(el('span', { class: 'presszone-comments-badge presszone-comments-badge--danger' }, __('BANNED', 'comments-press-zone')));
    }
    if (summary.active_mutes > 0) {
        statusBadges.push(el('span', { class: 'presszone-comments-badge presszone-comments-badge--link' }, __('MUTED', 'comments-press-zone')));
    }
    // Show WARNED badge if warned in last 7 days
    if (summary.active_warnings > 0 || (summary.last_action?.type === 'warning' && summary.last_action?.date)) {
        const lastWarningDate = summary.last_action?.date ? new Date(summary.last_action.date) : null;
        const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        if (lastWarningDate && lastWarningDate > oneWeekAgo) {
            statusBadges.push(el('span', { class: 'presszone-comments-badge presszone-comments-badge--warning' }, __('WARNED', 'comments-press-zone')));
        }
    }

    // User header with stats and issue warning button
    const userHeader = el('div', { class: 'presszone-comments-widget', style: 'margin-bottom: 16px; border-inline-start: none; background: var(--presszone-comments-surface-2); flex-wrap: nowrap;' },
        el('div', { class: 'presszone-comments-user-cell', style: 'flex: 1;' },
            el('img', { src: data.user?.avatar || user.avatar || '', class: 'presszone-comments-avatar presszone-comments-avatar--large' }),
            el('div', { class: 'presszone-comments-user-info' },
                el('div', { style: 'display: flex; align-items: center; gap: 8px;' },
                    el('h3', { style: 'margin: 0;' }, data.user?.display_name || user.display_name),
                    ...statusBadges
                ),
                el('p', { class: 'presszone-comments-user-handle', style: 'margin: 0;' }, '@' + (data.user?.username || user.username))
            )
        ),
        // Stats badges
        el('div', { style: 'display: flex; gap: 12px; margin-inline-end: 24px;' },
            renderStatBadge('⚠️', summary.total_warnings || 0, __('Warnings', 'comments-press-zone'), '#f59e0b'),
            renderStatBadge('🔇', summary.total_mutes || 0, __('Mutes', 'comments-press-zone'), '#3b82f6'),
            renderStatBadge('🚫', summary.total_bans || 0, __('Bans', 'comments-press-zone'), '#ef4444')
        ),
        Button({
            label: '⚠️ ' + __('Issue Warning / Mute / Ban', 'comments-press-zone'),
            variant: 'warning',
            onClick: () => showInfractionModal(user.id, container, { suggested: suggested })
        })
    );

    mount(container, userHeader);
    mount(container, escalationBar);
    if (suggestedCard) mount(container, suggestedCard);

    // Warnings list
    if (!data.warnings || data.warnings.length === 0) {
        mount(container, EmptyState(
            '📋',
            __('No Warnings Found', 'comments-press-zone'),
            __('This user has no warning history.', 'comments-press-zone')
        ));
        return;
    }

    const warningsList = el('div', { class: 'presszone-comments-moderation__list' },
        ...data.warnings.map(warning => renderWarningCard(warning, user.id, container))
    );
    mount(container, warningsList);
}

/**
 * Render escalation level steps visualization.
 */
function renderEscalationSteps(currentLevel) {
    const steps = [
        { label: __('Clean', 'comments-press-zone'), color: '#22c55e' },
        { label: __('Warn', 'comments-press-zone'), color: '#f59e0b' },
        { label: __('Mute', 'comments-press-zone'), color: '#f97316' },
        { label: __('Ban', 'comments-press-zone'), color: '#ef4444' },
        { label: __('Perm', 'comments-press-zone'), color: '#991b1b' }
    ];

    return steps.map((step, idx) => {
        const isActive = idx <= currentLevel;
        const isCurrent = idx === currentLevel;
        return el('div', { style: 'display: flex; flex-direction: column; align-items: center; gap: 4px;' },
            el('div', {
                style: `width: 24px; height: 24px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 10px; font-weight: 600; transition: all 0.2s;
                    ${isActive
                        ? `background: ${step.color}; color: white; box-shadow: 0 0 0 ${isCurrent ? '3px' : '0'} ${step.color}40;`
                        : 'background: var(--presszone-comments-surface-2); color: var(--presszone-comments-text-muted); border: 1px solid var(--presszone-comments-border);'}`
            }, idx === 0 ? '✓' : String(idx)),
            el('span', {
                style: `font-size: 10px; ${isActive ? 'color: ' + step.color + '; font-weight: 500;' : 'color: var(--presszone-comments-text-muted);'}`
            }, step.label),
            idx < steps.length - 1 ? el('div', {
                style: `position: absolute; inset-inline-start: calc(50% + 12px); top: 12px; width: 20px; height: 2px; background: ${idx < currentLevel ? step.color : 'var(--presszone-comments-border)'};`
            }) : null
        );
    }).reduce((acc, step, idx, arr) => {
        acc.push(step);
        if (idx < arr.length - 1) {
            acc.push(el('div', {
                style: `width: 24px; height: 2px; background: ${idx < currentLevel ? steps[idx].color : 'var(--presszone-comments-border)'}; margin-top: -16px;`
            }));
        }
        return acc;
    }, []);
}

/**
 * Render a stat badge.
 */
function renderStatBadge(icon, count, label, color) {
    return el('div', { style: 'text-align: center;' },
        el('div', {
            style: `font-size: 20px; font-weight: 700; color: ${count > 0 ? color : 'var(--presszone-comments-text-muted)'};`
        }, icon + ' ' + count),
        el('div', { style: 'font-size: 11px; color: var(--presszone-comments-text-muted);' }, label)
    );
}




function renderWarningCard(warning, userId, container) {
    const typeClasses = {
        warning: 'presszone-comments-badge--warning',
        mute: 'presszone-comments-badge--link',
        ban: 'presszone-comments-badge--danger',
        unban: 'presszone-comments-badge--success',
        unmute: 'presszone-comments-badge--success'
    };

    // Determine if action button should be shown and what action to take
    // - Warnings: always show "Clear warning" (X button) if active
    // - Mutes/Bans: only show Unmute/Unban if active AND not expired
    const canTakeAction = warning.is_active && !warning.is_expired;
    const isMuteOrBan = warning.type === 'mute' || warning.type === 'ban';

    // Build action button based on type
    let actionButton = null;
    if (canTakeAction) {
        if (warning.type === 'ban') {
            // Show "Unban" button for active bans
            actionButton = el('button', {
                class: 'presszone-comments-btn presszone-comments-btn--sm presszone-comments-btn--success',
                onclick: async () => {
                    if (await Confirm(__('Are you sure you want to unban this user?', 'comments-press-zone'), { okVariant: 'danger', icon: '⚠️' })) {
                        await API.post('/unban', { user_id: userId });
                        showUserWarnings({ id: userId }, container);
                    }
                }
            }, __('Unban', 'comments-press-zone'));
        } else if (warning.type === 'mute') {
            // Show "Unmute" button for active mutes
            actionButton = el('button', {
                class: 'presszone-comments-btn presszone-comments-btn--sm presszone-comments-btn--success',
                onclick: async () => {
                    if (await Confirm(__('Are you sure you want to unmute this user?', 'comments-press-zone'), { okVariant: 'danger', icon: '⚠️' })) {
                        await API.post('/unmute', { user_id: userId });
                        showUserWarnings({ id: userId }, container);
                    }
                }
            }, __('Unmute', 'comments-press-zone'));
        } else if (warning.type === 'warning') {
            // Show "Clear warning" (X) button for warnings
            actionButton = el('button', {
                class: 'presszone-comments-btn presszone-comments-btn--sm presszone-comments-btn--ghost',
                title: __('Clear warning', 'comments-press-zone'),
                onclick: async () => {
                    if (await Confirm(__('Are you sure you want to clear this warning?', 'comments-press-zone'), { okVariant: 'danger', icon: '⚠️' })) {
                        await API.delete(`/warnings/${warning.id}`);
                        showUserWarnings({ id: userId }, container);
                    }
                }
            }, '✕');
        }
    }

    return el('div', { class: `presszone-comments-moderation__card ${warning.is_active && !warning.is_expired ? '' : 'presszone-comments-node-item--inactive'}` },
        el('div', { class: 'presszone-comments-moderation__card-header' },
            el('span', { class: `presszone-comments-badge ${typeClasses[warning.type] || ''}` }, warning.type_label),
            el('span', { class: 'presszone-comments-moderation__date', style: 'flex: 1; text-align: center;' }, warning.created_at),
            el('div', { style: 'min-width: 80px; text-align: end;' }, actionButton)
        ),
        el('div', { class: 'presszone-comments-moderation__reason' },
            el('p', {}, warning.reason),
            // Display internal notes if present
            (warning.moderator_notes || warning.notes) && el('p', {
                style: 'margin-top: 8px; padding-top: 8px; border-top: 1px dashed var(--presszone-comments-border); font-size: 12px; color: var(--presszone-comments-text-muted); font-style: italic;'
            },
                el('span', { style: 'font-weight: 500; font-style: normal;' }, '🔒 ' + __('Internal Note:', 'comments-press-zone') + ' '),
                warning.moderator_notes || warning.notes
            )
        ),
        el('p', { class: 'presszone-comments-moderation__meta' },
            __('Issued by', 'comments-press-zone') + ' ' + warning.issued_by_name,
            warning.expires_at && !warning.is_expired
                ? ' • ' + __('Expires', 'comments-press-zone') + ': ' + warning.expires_at
                : ''
        )
    );
}

export default { renderModeration };

// Event listener for infraction success to refresh UI
window.addEventListener('presszone-infraction-success', (e) => {
    const { userId, type, resultsContainer } = e.detail;

    // Refresh table if present
    const tableId = `${type}-table-container`;
    const tableContainer = document.getElementById(tableId);
    if (tableContainer) {
        loadInfractionUsers(tableContainer, type);
    }

    // Refresh user warnings if resultsContainer is provided and valid
    if (resultsContainer && document.body.contains(resultsContainer)) {
        showUserWarnings({ id: userId }, resultsContainer);
    }

    // Refresh Overview stats and activity if on Overview tab
    if (activeTab === 'overview') {
        // Find the panel content wrapper
        const panel = document.querySelector('.presszone-comments-moderation__panel');
        if (panel) {
            panel.innerHTML = '';
            renderOverviewTab(panel);
        }
    }
});
